
import { useState, useCallback, useEffect } from "react";
import Header from "@/components/Header";
import { deleteEntry, syncLocalEntries, DiaryEntry } from "@/utils/diary";
import { toast } from "sonner";
import { useAuth } from "@/contexts/auth";
import { useLanguage } from "@/contexts/language";
import { useEntriesData } from "@/hooks/useEntriesData";

// Import our components
import EntriesHeader from "@/components/entries/EntriesHeader";
import TipOfTheDay from "@/components/entries/TipOfTheDay";
import GuestPrompt from "@/components/entries/GuestPrompt";
import EntriesCalendar from "@/components/entries/EntriesCalendar";
import EntriesList from "@/components/entries/EntriesList";

const Entries = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(undefined);
  const { user } = useAuth();
  const { language } = useLanguage();
  
  const { 
    entries, 
    isLoading, 
    error, 
    refreshEntries 
  } = useEntriesData(user?.id);
  
  // Sync local entries in a separate effect
  useEffect(() => {
    let isMounted = true;
    
    if (user?.id) {
      // This runs in parallel with other operations
      (async () => {
        try {
          await syncLocalEntries(user.id);
          // Only refresh if component is still mounted and there are likely new entries
          if (isMounted) {
            refreshEntries();
          }
        } catch (error) {
          console.error("Error syncing entries:", error);
        }
      })();
    }
    
    return () => {
      isMounted = false;
    };
  }, [user, refreshEntries]);
  
  const handleDelete = useCallback(async (id: string) => {
    if (!user) {
      toast.error(language === 'ja' ? "エントリーを削除するにはログインが必要です" : "You need to sign in to delete entries");
      return;
    }
    
    try {
      await deleteEntry(id);
      refreshEntries(); // Refresh entries list after deletion
      toast.success(language === 'ja' ? "エントリーが削除されました" : "Entry deleted successfully!");
    } catch (error) {
      console.error("Error deleting entry:", error);
      toast.error(language === 'ja' 
        ? "エントリーの削除中にエラーが発生しました" 
        : "Error deleting entry");
    }
  }, [user, language, refreshEntries]);
  
  const handleUpdate = useCallback((updatedEntry: DiaryEntry) => {
    if (!user) {
      toast.error(language === 'ja' ? "エントリーを更新するにはログインが必要です" : "You need to sign in to update entries");
      return;
    }
    
    refreshEntries(); // Simply refresh all entries after update
  }, [user, language, refreshEntries]);
  
  return (
    <main className="min-h-screen pt-24 pb-16">
      <Header />
      <div className="container max-w-4xl mx-auto px-4">
        <EntriesHeader 
          searchTerm={searchTerm} 
          setSearchTerm={setSearchTerm} 
        />
        
        <TipOfTheDay />
        
        {!user && <GuestPrompt type="alert" />}
        
        {error ? (
          <div className="text-center p-12 border rounded-lg bg-destructive/10 shadow-sm">
            <h3 className="font-medium text-lg text-destructive">{error}</h3>
            <button 
              onClick={refreshEntries} 
              className="mt-4 px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors"
            >
              {language === 'ja' ? "再試行" : "Try Again"}
            </button>
          </div>
        ) : user ? (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6 animate-fade-up animation-delay-100">
            <div className="md:col-span-1">
              <EntriesCalendar 
                entries={entries}
                selectedDate={selectedDate}
                setSelectedDate={setSelectedDate}
              />
            </div>
            
            <div className="md:col-span-2">
              <EntriesList 
                entries={entries}
                onDelete={handleDelete}
                onUpdate={handleUpdate}
                searchTerm={searchTerm}
                selectedDate={selectedDate}
                isLoading={isLoading}
              />
            </div>
          </div>
        ) : (
          <GuestPrompt type="fullPage" />
        )}
      </div>
    </main>
  );
};

export default Entries;
